/**
 * Pure AI Plan button phase/title resolution — mirrors iOS AIPlanButtonStateResolver.
 */

export type AIPlanButtonPhase = 'ready' | 'creatingAndWaiting' | 'parsing' | 'formatting';

export interface AIPlanButtonInputs {
  hasPlanData: boolean;
  aiStatus: string | null | undefined;
  aiJobId: string | null | undefined;
  aiJobStartedAt: string | Date | null | undefined;
  isQueued: boolean;
}

export interface AIPlanButtonDisplayState {
  title: string;
  isEnabled: boolean;
}

export const AI_PLAN_BUTTON_MAX_POLLING_MS = 300_000;

export const AI_PLAN_BUTTON_TITLES = {
  ready: 'AI Plan',
  creating: 'Creating Plan',
  parsing: 'Parsing Plan',
  formatting: 'Formatting Plan',
} as const;

function normalizeStatus(status: string | null | undefined): string | null {
  const trimmed = String(status ?? '').trim().toLowerCase();
  return trimmed || null;
}

function startedAtMs(value: string | Date | null | undefined): number | null {
  if (!value) return null;
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isFinite(t) ? t : null;
  }
  const t = Date.parse(String(value));
  return Number.isFinite(t) ? t : null;
}

export function isPlanComplete(inputs: AIPlanButtonInputs): boolean {
  if (inputs.hasPlanData) return true;
  return normalizeStatus(inputs.aiStatus) === 'done';
}

export function isPlanFailed(inputs: AIPlanButtonInputs): boolean {
  return normalizeStatus(inputs.aiStatus) === 'failed';
}

export function hasTimedOut(inputs: AIPlanButtonInputs, now: Date = new Date()): boolean {
  const started = startedAtMs(inputs.aiJobStartedAt);
  if (started == null) return false;
  return now.getTime() - started >= AI_PLAN_BUTTON_MAX_POLLING_MS;
}

export function isPlanInFlight(inputs: AIPlanButtonInputs, now: Date = new Date()): boolean {
  if (isPlanComplete(inputs) || isPlanFailed(inputs) || hasTimedOut(inputs, now)) return false;
  if (inputs.isQueued) return true;

  const jobId = String(inputs.aiJobId ?? '').trim();
  const started = startedAtMs(inputs.aiJobStartedAt);
  if (!jobId || started == null) return false;
  return now.getTime() - started < AI_PLAN_BUTTON_MAX_POLLING_MS;
}

export function initialAIPlanButtonPhase(
  inputs: AIPlanButtonInputs,
  now: Date = new Date()
): AIPlanButtonPhase {
  if (isPlanComplete(inputs) || isPlanFailed(inputs) || hasTimedOut(inputs, now)) return 'ready';
  if (isPlanInFlight(inputs, now)) return 'creatingAndWaiting';
  return 'ready';
}

export function resolveAIPlanButtonState(
  inputs: AIPlanButtonInputs,
  phase: AIPlanButtonPhase,
  now: Date = new Date(),
  titles: typeof AI_PLAN_BUTTON_TITLES = AI_PLAN_BUTTON_TITLES
): AIPlanButtonDisplayState {
  if (isPlanComplete(inputs) || isPlanFailed(inputs) || hasTimedOut(inputs, now)) {
    return { title: titles.ready, isEnabled: true };
  }

  if (isPlanInFlight(inputs, now)) {
    if (phase === 'parsing') {
      return { title: titles.parsing, isEnabled: false };
    }
    if (phase === 'formatting') {
      return { title: titles.formatting, isEnabled: false };
    }
    return { title: titles.creating, isEnabled: false };
  }

  return { title: titles.ready, isEnabled: true };
}
